import { useEffect, useState } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/roles";
import { ShieldCheck, Monitor, MapPin, AlertTriangle, Globe } from "lucide-react";

function Flags({ row }) {
  if (row.is_first) {
    return <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded bg-[#15233B]/8 text-[#15233B]/60">First sign-in</span>;
  }
  if (!row.is_new_device && !row.is_new_location) {
    return <span className="text-[10px] font-semibold text-[#16A34A]">Recognized</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {row.is_new_device && (
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded bg-[#b45309]/12 text-[#b45309] inline-flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> New device
        </span>
      )}
      {row.is_new_location && (
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded bg-[#b91c1c]/12 text-[#b91c1c] inline-flex items-center gap-1">
          <MapPin className="h-3 w-3" /> New location
        </span>
      )}
    </span>
  );
}

function LoginTable({ rows, showUser, testId }) {
  return (
    <div className="bg-white border border-[#15233B]/10 rounded-md shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60 border-b border-[#15233B]/10 bg-[#F7F9FC]">
            <tr>
              <th className="text-left px-4 py-2.5">When</th>
              {showUser && <th className="text-left px-4 py-2.5">User</th>}
              <th className="text-left px-4 py-2.5">Device</th>
              <th className="text-left px-4 py-2.5">Location</th>
              <th className="text-left px-4 py-2.5">IP</th>
              <th className="text-left px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#15233B]/8">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`login-row-${r.id}`} className={r.is_new && !r.is_first ? "bg-[#FFF7ED]" : ""}>
                <td className="px-4 py-2.5 whitespace-nowrap text-[#15233B]/80 font-medium">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                {showUser && (
                  <td className="px-4 py-2.5">
                    <div className="font-bold text-[#15233B]">{r.user_name}</div>
                    <div className="text-[11px] text-[#15233B]/45 font-mono">{r.user_email}</div>
                  </td>
                )}
                <td className="px-4 py-2.5 text-[#15233B]/80">
                  <span className="inline-flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5 text-[#1E73C9]" />{r.device_label}</span>
                </td>
                <td className="px-4 py-2.5 text-[#15233B]/80">
                  <span className="inline-flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-[#1E73C9]" />{r.location_label || "Unknown"}</span>
                </td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-[#15233B]/55">{r.ip}</td>
                <td className="px-4 py-2.5"><Flags row={r} /></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={showUser ? 6 : 5} className="px-4 py-10 text-xs italic text-[#15233B]/40 text-center">
                  No sign-in activity yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SecurityPage() {
  const { user } = useAuth();
  const owner = isOwner(user);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);

  useEffect(() => {
    api.get("/security/my-logins", { params: { limit: 50 } }).then(({ data }) => setMine(data)).catch(() => setMine([]));
    if (owner) {
      api.get("/security/logins", { params: { limit: 200 } }).then(({ data }) => setAll(data)).catch(() => setAll([]));
    }
  }, [owner]);

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div data-testid="security-page" className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl w-full mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-[#FBBF24]">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold">Security · Sign-in activity</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Sign-in activity</h1>
          <p className="text-sm text-[#15233B]/60">
            Review recent sign-ins. If you see a <span className="font-semibold text-[#b91c1c]">new device</span> or{" "}
            <span className="font-semibold text-[#b91c1c]">new location</span> you don't recognize, change your password immediately.
          </p>
        </div>

        <section className="mb-8">
          <h2 className="text-base font-bold text-[#15233B] mb-2">Your recent sign-ins</h2>
          <LoginTable rows={mine} showUser={false} testId="my-logins-table" />
        </section>

        {owner && (
          <section data-testid="all-logins-section">
            <h2 className="text-base font-bold text-[#15233B] mb-2">All team sign-ins <span className="text-xs font-normal text-[#15233B]/45">(owner monitor)</span></h2>
            <LoginTable rows={all} showUser={true} testId="all-logins-table" />
          </section>
        )}
      </div>
      <BrandFooter />
    </div>
  );
}
